import {
    Badge,
    Box,
    Button,
    FileInput,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { getUserApiKeys, getUsers } from '../../../../../http/apiClient';
import { useEffect, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchableSelect } from '../../../../common';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../../utils/validation';
import { hasRole } from '../../../../../utils/roles';
import useAddMessageGroup from '../../../../../http/mutations/groups/useAddMessageGroup';
import useAddShortLink from '../../../../../http/mutations/resources/useAddShortLink';
import { useAuthStore } from '../../../../../store';
import { useForm } from '@mantine/form';

const CreateDomainModal = ({ opened, close }) => {
    const userProfile = useAuthStore((state) => state.userProfile);

    const userQueryHttpFn = (queryHttpFn, queryParams) => {
        const userId = form.values.userId;
        const searchUserRoutes = hasRole(userProfile, 'SUPER_ADMIN') && userId;

        if (searchUserRoutes) {
            return queryHttpFn({
                userId,
                pagination: queryParams,
            });
        }
        return queryHttpFn({
            pagination: queryParams,
        });
    };

    const getInitialValues = () => ({
        domain: '',
        userId: hasRole(userProfile, 'SUPER_ADMIN')
            ? userProfile?.id.toString()
            : null,
        registrarApiKey: '1',
        dnsApiKey: '',
        manual: true,
    });

    const form = useForm({
        initialValues: getInitialValues(),
        validateInputOnBlur: true,
        validate: {
            domain: (value) => {
                const { type, message } = genericValidator(
                    'Domain name',
                    value,
                    {
                        required: true,
                        pattern:
                            /^(?:[a-zA-Z0-9-]+\.)+(?:[a-zA-Z]{2,}|xn--[a-zA-Z0-9-]+)$/,
                    }
                );
                if (type) {
                    return type === 'pattern' ? 'Invalid domain name' : message;
                }
            },
            userId: (value) => {
                const { message } = genericValidator('User ID', value, {
                    required: hasRole(userProfile, 'SUPER_ADMIN'),
                });
                return message;
            },
        },
        transformValues: (values) => ({
            ...values,
            dnsApiKey: Number(values.dnsApiKey) || null,
        }),
    });

    useEffect(() => {
        form.setInitialValues(getInitialValues());
        form.reset();
    }, [opened]);

    const { isPending, mutate: addOrUpdate } = useAddShortLink();

    const onSubmit = (model) => {
        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>Create Domain</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <TextInput
                                label={'Domain Name'}
                                placeholder='Enter a domain name'
                                withAsterisk
                                key={form.key('domain')}
                                {...form.getInputProps('domain')}
                            />

                            {hasRole(userProfile, 'SUPER_ADMIN') ? (
                                <SearchableSelect
                                    label={'User'}
                                    withAsterisk
                                    placeholder='User'
                                    queryHttpFn={getUsers}
                                    queryKey={'users'}
                                    optionsMapFn={(user) => ({
                                        label: user.username,
                                        value: user.id.toString(),
                                    })}
                                    key={form.key('userId')}
                                    {...form.getInputProps('userId')}
                                />
                            ) : null}

                            <SearchableSelect
                                label={'Domain Registrar'}
                                withAsterisk
                                placeholder='Select domain registrar'
                                queryHttpFn={(queryParams) =>
                                    userQueryHttpFn(getUserApiKeys, {
                                        ...queryParams,
                                        filters: ['registrar'],
                                    })
                                }
                                queryKey={
                                    form.values.userId
                                        ? [
                                              'api_keys',
                                              'registrar',
                                              form.values.userId,
                                          ]
                                        : ['api_keys', 'registrar']
                                }
                                optionsMapFn={(route) => ({
                                    label: route.label,
                                    value: route.value.toString(),
                                })}
                                key={form.key('registrarApiKey')}
                                {...form.getInputProps('registrarApiKey')}
                            />

                            <SearchableSelect
                                label={'DNS Provider'}
                                withAsterisk
                                placeholder='Select DNS provider'
                                queryHttpFn={(queryParams) =>
                                    userQueryHttpFn(getUserApiKeys, {
                                        ...queryParams,
                                        filters: ['dns'],
                                    })
                                }
                                queryKey={
                                    form.values.userId
                                        ? [
                                              'api_keys',
                                              'dns',
                                              form.values.userId,
                                          ]
                                        : ['api_keys', 'dns']
                                }
                                optionsMapFn={(x) => ({
                                    label: x.label,
                                    value: x.value.toString(),
                                })}
                                defaultValue={{
                                    label: 'Random Cloudflare',
                                    value: '',
                                }}
                                key={form.key('dnsApiKey')}
                                {...form.getInputProps('dnsApiKey')}
                            />

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={isPending || !form.isValid()}
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    Create
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default CreateDomainModal;
